import authApi from '@api/authApi';
import statsApi from '@api/statsApi';
import DefaultPage from '@components/defaultPage';
import Title from '@components/title';
import { useTranslator } from '@i18n';
import CatchStats from '@routes/_home/-catchStats';
import DashboardFilter from '@routes/_home/-dashboardFilter';
import { dashboardDateFilter, dashboardFilter } from '@routes/_home/-dashboardFilters';
import DateFilter from '@routes/_home/-dateFilter';
import TopMetrics from '@routes/_home/-topMetrics';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { useMemo } from 'react';

export const Route = createFileRoute('/_home/')({
  component: HomePage,
});

let defaultTimeZone = 'Europe/Berlin';
try {
  defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
} catch {
  // ignore
}

function HomePage() {
  const t = useTranslator();
  const { locationId } = useSearch({ strict: false });
  const [filter] = dashboardFilter.useProp();
  const dateFilter = dashboardDateFilter.useStore();
  const [me] = authApi.me.useCache();
  const timeZone = me?.user.timezone ?? defaultTimeZone;
  const now = useMemo(() => new Date(), []);

  const [stats, error] = statsApi
    .stats({
      minDate:
        dateFilter?.min &&
        Temporal.PlainDate.from({
          year: dateFilter.min.getFullYear(),
          month: dateFilter.min.getMonth() + 1,
          day: dateFilter.min.getDate(),
        }).toString(),
      maxDate:
        dateFilter?.max &&
        Temporal.PlainDate.from({
          year: dateFilter.max.getFullYear(),
          month: dateFilter.max.getMonth() + 1,
          day: dateFilter.max.getDate(),
        }).toString(),
      timeZone,
      ...(locationId ? { include: [locationId] } : filter),
    })
    .useCache({ disabled: !dateFilter });
  useHandleError(error);

  return (
    <Title title={t('overview.link')}>
      <DefaultPage
        css={{
          flex: 0,
          padding: 'var(--mantine-spacing-md)',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--mantine-spacing-md)',
          justifyContent: 'center',

          '& > h1': {
            marginTop: 'var(--mantine-spacing-md)',
            gridColumn: '1 / -1',
            fontWeight: 'bold',

            '& > span': {
              marginLeft: '1rem',
              fontSize: '1rem',
              fontWeight: 'normal',
              display: 'inline-block',
            },
          },
        }}
      >
        <DashboardFilter />

        <h1>
          {t('overview.current')} <span>{t.dateTimeFormat(now, { dateStyle: 'long' })}</span>
        </h1>
        <TopMetrics stats={stats} />

        <h1>
          {t('overview.trend')}{' '}
          <span>
            <DateFilter />
          </span>
        </h1>
        <CatchStats stats={stats} />
      </DefaultPage>
    </Title>
  );
}
